import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { useCallback, useState } from 'react';

import { DEFAULT_JOB_TAG_ICON } from './icons';
import { IconSwatchPicker } from './icon-swatch-picker';
import { JobTagChip } from './job-tag-chip';
import { SpeciesSelect } from './species-select';
import type { JobTagRow } from './use-job-tags';

interface JobTagSettingsRowProps {
  tag: JobTagRow;
  /** Resolves `false` when the write was rejected (e.g. a duplicate name). */
  onSave: (id: string, patch: Partial<JobTagRow>) => Promise<boolean>;
  onDelete: (tag: JobTagRow) => void;
}

/**
 * One editable tag in the settings panel. Icon, species and the enabled
 * switch commit immediately; the name commits on blur (or Enter) so a rename
 * isn't a mutation per keystroke.
 * @param root0
 * @param root0.tag
 * @param root0.onSave
 * @param root0.onDelete
 */
export function JobTagSettingsRow({
  tag,
  onSave,
  onDelete,
}: JobTagSettingsRowProps) {
  // What the user has typed, or `null` when they haven't touched the field —
  // in which case it shows the tag's CURRENT name straight from the query.
  //
  // Deliberately not a copy of `tag.name` taken once at mount. A copy goes
  // stale as soon as the name changes underneath it (someone else renames the
  // tag and this list refetches), and blurring a stale field then "commits"
  // the old name back over their rename without anyone having typed a thing.
  const [draft, setDraft] = useState<string | null>(null);

  const commitName = useCallback(async () => {
    // Untouched: nothing to commit, whatever the name is now.
    if (draft === null) return;

    const next = draft.trim();
    if (next && next !== tag.name) {
      // The save awaits the list refetch, so by the time it settles `tag.name`
      // is the accepted name — or still the old one if the rename was
      // rejected, which is exactly what the field should snap back to.
      await onSave(tag._id, { name: next });
    }
    setDraft(null);
  }, [draft, tag._id, tag.name, onSave]);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <IconSwatchPicker
          value={tag.icon || DEFAULT_JOB_TAG_ICON}
          label={`Icon for "${tag.name}"`}
          onChange={(icon) => onSave(tag._id, { icon })}
        />

        <TextField
          size="small"
          label="Name"
          value={draft ?? tag.name}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // This panel is a tab inside the processor settings <Form>;
              // without this, Enter also fires its submit button and saves
              // every processor setting.
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          sx={{ flex: 1, minWidth: 0 }}
        />

        <SpeciesSelect
          value={tag.species}
          onChange={(species) => onSave(tag._id, { species })}
        />

        <JobTagChip tag={tag} sx={{ fontWeight: 600, maxWidth: 180 }} />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={tag.isEnabled}
              onChange={(e) => onSave(tag._id, { isEnabled: e.target.checked })}
            />
          }
          label={tag.isEnabled ? 'Enabled' : 'Disabled'}
          sx={{ mr: 0, whiteSpace: 'nowrap' }}
        />

        <Tooltip title="Delete tag">
          <IconButton
            size="small"
            color="error"
            onClick={() => onDelete(tag)}
            aria-label={`Delete "${tag.name}"`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}
